export type ProfileFieldValues = { [key: string]: unknown };

const ANGEL_INVESTOR = "Angel Investor";
const SECTOR_COMPANY_TYPES = ["Startup", "Scaleup"];

export const isAnswered = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return Boolean(value);
};

export const getApplicableFields = (
  values: ProfileFieldValues
): { [key: string]: boolean } => {
  const role = values["form_role_at_business"];
  const companyType = values["form_company_type"];
  const hasCompany = role !== ANGEL_INVESTOR;

  return {
    first_name: true,
    last_name: true,
    form_role_at_business: true,
    form_country: true,
    form_company_name: hasCompany,
    form_company_type: hasCompany,
    form_sector:
      hasCompany && SECTOR_COMPANY_TYPES.includes(companyType as string),
    form_professional_service_type:
      hasCompany && companyType === "Professional Services",
    form_investment_firm_type: hasCompany && companyType === "Investment Firm",
    form_advisory_firm_type: hasCompany && companyType === "Advisory Firm",
  };
};

export const hasIncompleteProfile = (values: ProfileFieldValues): boolean =>
  Object.entries(getApplicableFields(values)).some(
    ([name, applicable]) => applicable && !isAnswered(values[name])
  );

export const hasAnyAnswer = (values: ProfileFieldValues): boolean =>
  Object.values(values).some(isAnswered);

export type FieldElements = { [key: string]: HTMLFormElement };

export const readFieldValues = (
  fields: FieldElements
): { [key: string]: string | boolean } =>
  Object.fromEntries(
    Object.entries(fields).map(([name, field]) => [
      name,
      field.type === "checkbox" ? field.checked : field.value,
    ])
  );

export const applyFieldVisibility = (
  baseValues: ProfileFieldValues,
  fields: FieldElements
): void => {
  const answeredFieldValues = Object.fromEntries(
    Object.entries(readFieldValues(fields)).filter(([, value]) =>
      isAnswered(value)
    )
  );
  const values = { ...baseValues, ...answeredFieldValues };
  const applicable = getApplicableFields(values);

  Object.entries(applicable).forEach(([name, visible]) => {
    if (fields[name]) {
      fields[name].dataset.show = visible ? "true" : "false";
    }
  });
};
